import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import useOrgCurrency from '../../Hooks/useOrgCurrency';

const EM_DASH = '—';
const MAX_DAYS_VISUAL = 180;

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatDDMMYYYY = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const ContractsRequiringAttentionWidget = ({ onContractClick }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);
  const currency = useOrgCurrency();

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const rows = data?.contractsRequiringAttention || [];

  const expiredSummary = useMemo(() => {
    const expired = rows.filter((c) => c?.derivedStatus === 'EXPIRED');
    const expiredCount = expired.length;
    const uncoveredValue = expired.reduce((sum, c) => sum + (Number(c?.contractValue) || 0), 0);
    return { expiredCount, uncoveredValue };
  }, [rows]);

  const isEmpty = rows.length === 0;

  return (
    <ReportWidget
      title={
        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap w-100">
          <div>
            <span>{t('business.inventory.contracts_requiring_attention', 'Contracts Requiring Attention')}</span>
            <div className="text-muted small fw-normal">
              {t('business.inventory.contracts_requiring_attention_hint', 'Expiring within 18 months')}
            </div>
          </div>
          {expiredSummary.expiredCount > 0 && (
            <span
              className="fw-medium"
              style={{ padding: '4px 10px', borderRadius: 999, fontSize: '0.8rem', backgroundColor: '#FEE2E2', color: '#B91C1C' }}
            >
              {t('business.inventory.summary_expired_uncovered', {
                count: expiredSummary.expiredCount,
                amount: formatMoney(expiredSummary.uncoveredValue, currency),
                defaultValue: '{{count}} expired · {{amount}} uncovered',
              })}
            </span>
          )}
        </div>
      }
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('business.inventory.no_contracts_requiring_attention', 'No contracts require attention right now.')}
      onRefresh={handleRefresh}
    >
      <div className="row g-3">
        {rows.map((c) => {
          const isExpired = c?.derivedStatus === 'EXPIRED';
          const days = c?.daysRemaining ?? 0;
          const absDays = Math.abs(days);
          const fillPct = Math.min(absDays / MAX_DAYS_VISUAL, 1) * 100;
          const barColor = isExpired ? '#DC3545' : '#F59E0B';
          const pillBg = isExpired ? '#FEE2E2' : '#FEF3C7';
          const pillFg = isExpired ? '#B91C1C' : '#92400E';
          const pillText = isExpired
            ? t('business.inventory.days_expired', { count: absDays, defaultValue: '{{count}} days expired' })
            : t('business.inventory.days_left', { count: absDays, defaultValue: '{{count}} days left' });
          const formattedEnd = formatDDMMYYYY(c?.endDate);
          const leftLabel = isExpired
            ? t('business.inventory.expired_date', { date: formattedEnd, defaultValue: 'Expired {{date}}' })
            : t('business.inventory.today', 'Today');
          const rightLabel = isExpired
            ? t('business.inventory.today', 'Today')
            : t('business.inventory.exp_date', { date: formattedEnd, defaultValue: 'Exp {{date}}' });
          const typeAndVendor = [c?.categoryName, c?.vendorName].filter(Boolean).join(' · ');

          return (
            <div key={c.contractId} className="col-12 col-md-6">
              <div
                role="button"
                tabIndex={0}
                onClick={() => c?.contractId && onContractClick && onContractClick(c.contractId)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && c?.contractId && onContractClick) onContractClick(c.contractId);
                }}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: 12,
                  padding: 16,
                  cursor: c?.contractId ? 'pointer' : 'default',
                  borderLeft: `5px solid ${barColor}`,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  height: '100%',
                }}
              >
                <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
                  <div>
                    <div className="fw-semibold" style={{ color: '#2563EB' }}>
                      {c?.contractNumber || t('shared.n_a', 'N/A')}
                    </div>
                    {typeAndVendor && <div className="text-muted small">{typeAndVendor}</div>}
                  </div>
                  <span
                    className="fw-medium"
                    style={{ padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem', backgroundColor: pillBg, color: pillFg, whiteSpace: 'nowrap' }}
                  >
                    {pillText}
                  </span>
                </div>

                <div className="mt-3 mb-3">
                  <div style={{ position: 'relative', height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                    {isExpired ? (
                      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${fillPct}%`, background: barColor }} />
                    ) : (
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${fillPct}%`, background: barColor }} />
                    )}
                  </div>
                  <div className="d-flex justify-content-between small mt-1" style={{ color: isExpired ? '#B91C1C' : '#4B5563' }}>
                    <span>{leftLabel}</span>
                    <span>{rightLabel}</span>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-end mt-2">
                  <div>
                    <div className="text-muted small">{t('business.inventory.contract_value', 'Contract Value')}</div>
                    <div className="fw-semibold" style={{ fontSize: '1.1rem' }}>
                      {c?.contractValue != null ? formatMoney(c.contractValue, currency) : EM_DASH}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="text-muted small">{t('business.inventory.downtime_on_covered', 'Downtime on covered')}</div>
                    <div className="fw-semibold" style={{ fontSize: '1.1rem' }}>
                      {c?.unplannedDowntimeHoursOnCoveredAssets != null
                        ? `${c.unplannedDowntimeHoursOnCoveredAssets} h`
                        : EM_DASH}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </ReportWidget>
  );
};

export default ContractsRequiringAttentionWidget;
